import { useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { useSetError } from '@src/redux/modals';
import { Size } from '@Components/designConstants';
import { NEWTON_HOST, saveNewtonProjectMapping } from './newton-api';
import { isValidNewtonSlug } from './newton-picker-utils';

interface NewtonProjectPickerProps {
  cid: string;
  companyName: string;
  onMapped: (projectSlug: string) => void;
}

// Internal-only: link an unmapped client to its Newton project, persist it, and route.
// Newton's projects API can't be listed from here (it requires an embed-session token), so the
// user fills in just the URL slug segment — the project id or name — of the client's Newton URL.
export const NewtonProjectPicker = ({ cid, companyName, onMapped }: NewtonProjectPickerProps) => {
  const setError = useSetError();
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);

  const cleaned = slug.trim();
  const valid = isValidNewtonSlug(cleaned);
  const showInvalid = cleaned !== '' && !valid;

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await saveNewtonProjectMapping(cid, cleaned);
      onMapped(cleaned);
    } catch (e) {
      setError({ message: `Couldn't save the Newton project: ${e.message}`, reportError: e });
      setSaving(false);
    }
  };

  return (
    <div className='newton-picker'>
      <div className='newton-picker-card'>
        <h2 className='newton-picker-title'>Connect {companyName} to a Newton project</h2>
        <p className='newton-picker-desc'>
          Open this client&apos;s dashboard in Newton and fill in the project id (or name) from its
          URL. It&apos;ll then be saved for everyone who views this client&apos;s Newton page.
        </p>
        <InputGroup className='newton-url-input'>
          <InputGroup.Text>https://{NEWTON_HOST}/project/</InputGroup.Text>
          <Form.Control
            aria-label='Newton project id or name'
            placeholder='39'
            value={slug}
            onChange={e => setSlug(e.target.value)}
            isInvalid={showInvalid}
          />
          <InputGroup.Text>/home</InputGroup.Text>
        </InputGroup>
        {showInvalid && (
          <p className='newton-picker-hint'>Enter just the id or name — no slashes or spaces.</p>
        )}
        <div className='newton-picker-actions'>
          <Button size={Size.LG} onClick={handleSave} disabled={!valid} loading={saving}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
